/**
 * useScreenOrientation - Device Orientation Detection Hook
 *
 * Monitors and reports device screen orientation changes for responsive
 * video player UI and optimal mobile viewing experiences.
 *
 * WHY THIS EXISTS:
 * Sports streaming on mobile devices requires adaptive UI based on orientation:
 * - Portrait: Standard UI with controls below video
 * - Landscape: Fullscreen video with overlay controls
 *
 * BUSINESS REQUIREMENTS:
 * - Auto-fullscreen video when rotating to landscape (industry standard)
 * - Preserve user's viewing context during rotation
 * - Optimize control placement for each orientation
 * - Match native mobile video app behavior (YouTube, Netflix pattern)
 *
 * MOBILE UX RATIONALE:
 * Users expect video players to intelligently respond to device rotation.
 * This is especially critical for live sports where immersion matters.
 * Manual fullscreen toggling during exciting moments breaks the experience.
 *
 * @returns {string} Current orientation type (e.g., "portrait-primary", "landscape-primary")
 * @example
 * const orientation = useScreenOrientation();
 * const isLandscape = orientation.includes('landscape');
 * // Auto-enter fullscreen in landscape mode
 */
import { useState, useEffect } from 'react';

export type OrientationType =
  | 'portrait-primary'
  | 'portrait-secondary'
  | 'landscape-primary'
  | 'landscape-secondary';

/**
 * Gets the current screen orientation from the browser API.
 *
 * WHY OPTIONAL CHAINING AND FALLBACK:
 * Screen Orientation API has limited browser support:
 * - Not available in older browsers
 * - Restricted in some WebView environments
 * - May be disabled by privacy settings
 *
 * FALLBACK STRATEGY:
 * "portrait-primary" as default makes sense because:
 * - Most mobile usage starts in portrait
 * - Safer to assume portrait for control layout
 * - Prevents landscape UI in uncertain states
 *
 * BROWSER COMPATIBILITY:
 * - Chrome/Edge: Full support
 * - Safari iOS: Limited support (uses deprecated window.orientation)
 * - Firefox: Full support
 * - WebView: Varies by implementation
 */
function getOrientation(): OrientationType {
  // WHY screen.orientation.type INSTEAD OF window.orientation:
  // window.orientation is deprecated and returns degrees (0, 90, -90, 180)
  // screen.orientation.type provides semantic values that are more meaningful
  // for UI decisions and future-proof our code
  return (window?.screen?.orientation?.type as OrientationType) ?? 'portrait-primary';
}

const useScreenOrientation = (): OrientationType => {
  // WHY INITIALIZE WITH getOrientation():
  // Prevents hydration mismatches in SSR scenarios and ensures
  // the initial render has the correct orientation without waiting
  // for an effect to run. Critical for immediate correct UI layout.
  const [orientation, setOrientation] = useState<OrientationType>(getOrientation());

  /**
   * Updates orientation state when device is rotated.
   *
   * WHY NOT USE event.target:
   * The orientationchange event doesn't provide the new orientation
   * in the event object consistently across browsers.
   * Safer to query the current state directly from the API.
   *
   * PERFORMANCE CONSIDERATION:
   * This handler is lightweight (single DOM read + setState).
   * No debouncing needed as orientation changes are infrequent
   * and users expect immediate UI response to rotation.
   */
  const updateOrientation = (): void => {
    setOrientation(getOrientation());
  };

  useEffect(() => {
    // WHY orientationchange AND NOT resize:
    // - orientationchange fires specifically for rotation
    // - resize fires for any viewport change (keyboard, browser chrome)
    // - Prevents unnecessary updates from non-rotation events
    // - Better battery performance on mobile devices
    window.addEventListener('orientationchange', updateOrientation);

    // CLEANUP RATIONALE:
    // Removing event listeners prevents:
    // - Memory leaks in single-page applications
    // - Duplicate handlers if component remounts
    // - Unnecessary processing after navigation
    return () => {
      window.removeEventListener('orientationchange', updateOrientation);
    };
  }, []); // Empty deps: Event handler doesn't need to change

  return orientation;
};

export default useScreenOrientation;
